import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage } from '@fortawesome/pro-light-svg-icons/faImage';
import { TypedMemo } from '../../../common/types';
import { Segmented, SegmentedOption } from '../../components/primitives/Segmented';
import { ScheduledPostFormat } from '../types';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Promote pillar; each part uses its own slice of it */
import styles from '../Promote.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ComposerFormatSectionProps {
  format: ScheduledPostFormat;
  /** A story is a picture or a clip, so it cannot go out without one. */
  hasMedia: boolean;
  onChangeFormat: (format: ScheduledPostFormat) => void;
}

/** A feed post, or a story that lives for a day. */
const ComposerFormatSectionComponent: React.FC<ComposerFormatSectionProps> = ({ format, hasMedia, onChangeFormat }) => {
  const intl = useIntl();

  const label = intl.formatMessage({ id: 'app.sml.promote.composer.format', defaultMessage: 'Format' });

  const options = useMemo<SegmentedOption<ScheduledPostFormat>[]>(
    () => [
      {
        value: 'post',
        label: intl.formatMessage({ id: 'app.sml.promote.composer.format.post', defaultMessage: 'Post' }),
      },
      {
        value: 'story',
        label: intl.formatMessage({ id: 'app.sml.promote.composer.format.story', defaultMessage: 'Story' }),
      },
    ],
    [intl]
  );

  return (
    <div className={styles.stack}>
      <span className={styles.sectionLabel}>{label}</span>
      <Segmented options={options} value={format} onChange={onChangeFormat} ariaLabel={label} />
      {format === 'story' ? (
        <p className={styles.planNote}>
          <FontAwesomeIcon icon={faImage} />
          {hasMedia
            ? intl.formatMessage({
                id: 'app.sml.promote.composer.format.storyNote',
                defaultMessage: 'A story goes out on Facebook and Instagram, and it is gone after 24 hours.',
              })
            : intl.formatMessage({
                id: 'app.sml.promote.composer.format.storyNeedsMedia',
                defaultMessage: 'A story needs a photo or a clip.',
              })}
        </p>
      ) : null}
    </div>
  );
};

export const ComposerFormatSection = TypedMemo(ComposerFormatSectionComponent);
